// src/components/FCMTokenDisplay.jsx
// كومبوننت لعرض ونسخ FCM Token بسهولة

import React, { useState, useEffect } from 'react';
import notificationService from '../services/notificationService';
import supabaseService from '../services/supabaseService';
import { toast } from 'react-hot-toast';

function FCMTokenDisplay() {
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    loadTokenInfo();
  }, []);

  const loadTokenInfo = async () => {
    // جلب معلومات Token
    const fcmToken = notificationService.fcmToken || localStorage.getItem('fcmToken');
    setToken(fcmToken || '');

    // جلب حالة الإذن
    setPermission(notificationService.permission);

    // جلب معلومات المستخدم
    const user = supabaseService.currentUser;
    if (user) {
      setUserInfo({
        id: user.id,
        email: user.email,
      });
    }
  };

  const refreshToken = async () => {
    setLoading(true);
    try {
      const user = supabaseService.currentUser;
      if (!user) {
        toast.error('يجب تسجيل الدخول أولاً');
        return;
      }

      // إعادة تهيئة الخدمة
      await notificationService.initialize(user.id);
      
      // الحصول على Token الجديد
      const newToken = notificationService.fcmToken || localStorage.getItem('fcmToken');
      
      if (newToken) {
        setToken(newToken);
        toast.success('تم تحديث Token بنجاح');
      } else {
        toast.error('فشل الحصول على Token. تحقق من Console');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      toast.error('حدث خطأ أثناء تحديث Token');
    } finally {
      setLoading(false);
    }
  };

  const copyToken = async () => {
    if (!token) {
      toast.error('لا يوجد Token للنسخ');
      return;
    }

    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      toast.success('تم نسخ Token إلى الحافظة');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Error copying token:', error);
      toast.error('فشل النسخ. حاول نسخه يدوياً');
    }
  };

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission === 'granted') {
        toast.success('تم منح إذن الإشعارات');
        await refreshToken();
      } else {
        toast.error('تم رفض إذن الإشعارات');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('فشل طلب الإذن');
    }
  };

  const getPermissionStatusColor = () => {
    switch (permission) {
      case 'granted': return '#4CAF50';
      case 'denied': return '#f44336';
      default: return '#FF9800';
    }
  };

  const getPermissionStatusText = () => {
    switch (permission) {
      case 'granted': return '✅ مسموح';
      case 'denied': return '❌ محظور';
      default: return '⏳ لم يُطلب بعد';
    }
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: '20px auto',
      padding: '20px',
      backgroundColor: '#fff',
      borderRadius: '10px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      fontFamily: 'Arial, sans-serif',
      direction: 'rtl'
    }}>
      <h2 style={{ 
        borderBottom: '2px solid #4CAF50', 
        paddingBottom: '10px',
        marginBottom: '20px'
      }}>
        🔑 Firebase Cloud Messaging Token
      </h2>

      {/* معلومات المستخدم */}
      {userInfo && (
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '15px'
        }}>
          <p style={{ margin: '5px 0' }}>
            <strong>👤 المستخدم:</strong> {userInfo.email}
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>🆔 User ID:</strong> 
            <code style={{ 
              backgroundColor: '#e0e0e0', 
              padding: '2px 8px', 
              borderRadius: '3px',
              fontSize: '12px',
              marginRight: '10px'
            }}>
              {userInfo.id}
            </code>
          </p>
        </div>
      )}

      {/* حالة الإذن */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '5px',
        marginBottom: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>🔔 حالة إذن الإشعارات:</strong>
          <span style={{
            marginRight: '10px',
            color: getPermissionStatusColor(),
            fontWeight: 'bold'
          }}>
            {getPermissionStatusText()}
          </span>
        </div>
        
        {permission !== 'granted' && (
          <button
            onClick={requestPermission}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            طلب الإذن
          </button>
        )}
      </div>

      {/* عرض Token */}
      <div style={{ marginBottom: '15px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '10px',
          fontWeight: 'bold',
          fontSize: '16px'
        }}>
          FCM Token:
        </label>
        
        {token ? (
          <textarea
            value={token}
            readOnly
            rows={5}
            style={{
              width: '100%',
              padding: '12px',
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: '12px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '5px',
              resize: 'vertical',
              direction: 'ltr',
              textAlign: 'left'
            }}
          />
        ) : (
          <div style={{
            padding: '20px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '5px',
            color: '#856404',
            textAlign: 'center'
          }}>
            ⚠️ لم يتم الحصول على Token بعد
            <br />
            <small>تأكد من تسجيل الدخول والسماح بالإشعارات</small>
          </div>
        )}
      </div>

      {/* أزرار التحكم */}
      <div style={{ 
        display: 'flex', 
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={copyToken}
          disabled={!token}
          style={{
            flex: '1',
            minWidth: '150px',
            padding: '12px 20px',
            backgroundColor: copied ? '#4CAF50' : '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: token ? 'pointer' : 'not-allowed',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: token ? 1 : 0.5,
            transition: 'all 0.3s'
          }}
        >
          {copied ? '✅ تم النسخ!' : '📋 نسخ Token'}
        </button>

        <button
          onClick={refreshToken}
          disabled={loading}
          style={{
            flex: '1',
            minWidth: '150px',
            padding: '12px 20px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? '⏳ جاري التحديث...' : '🔄 تحديث Token'}
        </button>
      </div>

      {/* معلومات إضافية */}
      <div style={{
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#e3f2fd',
        borderLeft: '4px solid #2196F3',
        borderRadius: '5px',
        fontSize: '14px'
      }}>
        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
          ℹ️ كيفية الاستخدام:
        </p>
        <ol style={{ margin: 0, paddingRight: '20px' }}>
          <li>انسخ الـ Token أعلاه</li>
          <li>اذهب إلى <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer">Firebase Console</a></li>
          <li>افتح مشروعك: <strong>khidma-app-7649c</strong></li>
          <li>Engage → Cloud Messaging</li>
          <li>Send your first message</li>
          <li>في Target: اختر <strong>"Send test message"</strong></li>
          <li>الصق الـ Token واضغط Test</li>
        </ol>
      </div>

      {/* من Console المتصفح */}
      <div style={{
        marginTop: '15px',
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '5px',
        fontSize: '13px'
      }}>
        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
          💻 من Console المتصفح (F12):
        </p>
        <code style={{
          display: 'block',
          padding: '10px',
          backgroundColor: '#263238',
          color: '#aed581',
          borderRadius: '5px',
          fontFamily: 'Consolas, Monaco, monospace',
          direction: 'ltr',
          textAlign: 'left'
        }}>
          localStorage.getItem('fcmToken')
        </code>
      </div>
    </div>
  );
}

export default FCMTokenDisplay;